/**
 * AI Insights Plugin
 *
 * Generates AI-powered insights from comprehensive business data
 * using all three API endpoints (clubs, valuations, player-sales).
 *
 * Features:
 * - Multiple LLM providers (Ollama, OpenAI, Anthropic)
 * - Comprehensive data analysis
 * - Health monitoring
 * - Configurable settings
 */

import React from 'react';
import {
  ReportsPlugin,
  PluginContext,
  ComprehensiveReportData,
} from '@/lib/platform/types/plugins';
import { buildComprehensivePrompt } from './prompt';
import { createProvider, AIConfig } from './providers/factory';
import { InsightsPanel } from './components/InsightsPanel';

import { pluginLogger } from '@/lib/platform/utils/logger';

export interface Insight {
  type: 'positive' | 'negative' | 'neutral';
  title: string;
  description: string;
}

export const aiInsightsPlugin: ReportsPlugin<AIConfig> = {
  manifest: {
    id: 'ai-insights',
    name: 'AI Insights',
    version: '1.0.0',
    minSystemVersion: '1.0.0',
    pluginApiVersion: '1.0',
    description:
      'AI Insights for Reports - Generate AI-powered business insights from comprehensive club data',
    author: 'Valiyou',
    homepage: 'https://themine.app/plugins/ai-insights',
  },

  capabilities: {
    requiredEndpoints: ['clubs', 'valuations', 'player-sales'],
    requiredPermissions: ['read:clubs', 'read:valuations', 'read:player-sales'],

    features: {
      reports: true,
      dashboard: true, // Can be reused in dashboard
      valuations: false,
      analytics: false,
      exports: false,
    },

    resourceLimits: {
      maxExecutionTimeMs: 15000, // 15 seconds
      maxApiCallsPerMinute: 60,
    },
  },

  lifecycle: {
    async onInstall() {
      pluginLogger.log('[AI Insights] Plugin installed');
    },

    async onUninstall() {
      pluginLogger.log('[AI Insights] Plugin uninstalled');
    },

    async onEnable() {
      pluginLogger.log('[AI Insights] Plugin enabled');
    },

    async onDisable() {
      pluginLogger.log('[AI Insights] Plugin disabled');
    },

    async healthCheck() {
      const config = aiInsightsPlugin.currentConfig;

      if (!config) {
        return {
          status: 'degraded',
          message: 'No LLM provider configured',
          lastCheck: new Date(),
        };
      }

      const provider = createProvider(config);

      if (!provider) {
        return {
          status: 'unhealthy',
          message: 'Failed to create LLM provider',
          lastCheck: new Date(),
        };
      }

      const available = await provider.isAvailable();

      return {
        status: available ? 'healthy' : 'degraded',
        message: available
          ? `${provider.name} provider responsive`
          : `${provider.name} provider unavailable`,
        lastCheck: new Date(),
        dependencies: {
          llm: available ? 'available' : 'unavailable',
        },
      };
    },
  },

  enabled: false,
  currentConfig: undefined,
  context: undefined,

  settings: {
    component: (() => null) as never, // Placeholder component
    schema: {
      type: 'object',
      properties: {
        provider: {
          type: 'string',
          enum: ['groq', 'gemini', 'openai', 'anthropic'],
        },
        model: { type: 'string' },
        openaiApiKey: { type: 'string' },
        anthropicApiKey: { type: 'string' },
        groqApiKey: { type: 'string' },
        geminiApiKey: { type: 'string' },
      },
      required: ['provider'],
    },
    defaultConfig: {
      provider: 'groq',
      model: 'openai/gpt-oss-120b',
      groqApiKey: '',
      openaiApiKey: '',
      anthropicApiKey: '',
      geminiApiKey: '',
    },
    migrations: {
      '0.9.0': (config: unknown) => ({
        ...(config as AIConfig),
        provider: (config as { llmProvider?: string }).llmProvider || 'groq',
      }),
    },
  },

  async initialize(context: PluginContext) {
    aiInsightsPlugin.context = context;

    // Load config from storage
    const storedConfig = await context.storage.getAll<AIConfig>();
    aiInsightsPlugin.currentConfig = storedConfig || aiInsightsPlugin.settings!.defaultConfig;

    // Subscribe to events
    context.events.subscribe('valuation.created', async (event) => {
      context.logger.info('New valuation created', event.data);
      // Could trigger automatic insight generation here
    });

    context.logger.info('AI Insights plugin initialized');
  },

  async cleanup() {
    if (aiInsightsPlugin.context) {
      aiInsightsPlugin.context.logger.info('AI Insights plugin cleaning up');
    }
    aiInsightsPlugin.context = undefined;
  },

  async renderInsights(data) {
    const context = aiInsightsPlugin.context;

    if (!context) {
      throw new Error('AI Insights plugin not initialized');
    }

    if (!context.apiClient) {
      context.logger.warn('API client not available - plugin requires an API key to be configured');
      return null;
    }

    const { locale, fromDate, toDate } = data;

    try {
      // Fetch comprehensive data from all three endpoints
      context.logger.info('Fetching comprehensive report data');

      const comprehensiveData = await context.apiClient.getComprehensiveReport(
        context.organizationId,
        {
          fromDate,
          toDate,
        }
      );

      // Generate insights
      const insights = await generateComprehensiveInsights(
        comprehensiveData,
        locale,
        aiInsightsPlugin.currentConfig!,
        context
      );

      if (!insights || insights.length === 0) {
        context.logger.warn('No insights generated');
        return null;
      }

      context.logger.info(`Generated ${insights.length} insights`);

      // Return InsightsPanel component
      return React.createElement(InsightsPanel, {
        insights,
        provider: aiInsightsPlugin.currentConfig!.provider,
        dataScope: {
          club: true,
          valuations: true,
          playerSales: true,
        },
      });
    } catch (error) {
      context.logger.error('Failed to generate insights', error as Error);
      return null;
    }
  },
};

/**
 * Generate insights from comprehensive data
 */
async function generateComprehensiveInsights(
  data: ComprehensiveReportData,
  locale: 'en' | 'da',
  config: AIConfig,
  context: PluginContext
): Promise<Insight[]> {
  const provider = createProvider(config);

  if (!provider) {
    context.logger.error('No LLM provider configured');
    return [];
  }

  // Check availability
  const available = await provider.isAvailable();
  if (!available) {
    context.logger.warn(`${provider.name} provider not available`);
    return [];
  }

  try {
    // Build comprehensive prompt
    const prompt = buildComprehensivePrompt(data, locale);

    context.logger.debug('Generating insights with prompt', { promptLength: prompt.length });

    // Generate insights
    const response = await provider.generateInsights(prompt, {
      timeout: 15000, // 15 seconds
      maxTokens: 1000,
      temperature: 0.7,
    });

    // Parse JSON response
    const insights = parseInsightsResponse(response);

    if (!insights || insights.length === 0) {
      throw new Error('Invalid insights format');
    }

    return insights;
  } catch (error) {
    context.logger.error('Error generating insights', error as Error);
    return [];
  }
}

/**
 * Parse LLM response into insights array
 */
function parseInsightsResponse(response: string): Insight[] {
  try {
    // Try to extract JSON from response (some models include extra text)
    const jsonMatch = response.match(/\[[\s\S]*\]/);
    const jsonString = jsonMatch ? jsonMatch[0] : response;

    const parsed = JSON.parse(jsonString);

    if (!Array.isArray(parsed)) {
      throw new Error('Response is not an array');
    }

    // Validate each insight
    const insights: Insight[] = parsed.map((item: Record<string, unknown>) => {
      if (!item.type || !item.title || !item.description) {
        throw new Error('Invalid insight structure');
      }

      return {
        type: item.type as 'positive' | 'negative' | 'neutral',
        title: item.title as string,
        description: item.description as string,
      };
    });

    return insights;
  } catch (error) {
    pluginLogger.error('[AI Insights] Failed to parse response:', error);
    pluginLogger.error('[AI Insights] Response:', response);
    return [];
  }
}
